import { SortDirection, type SortField } from '@air/api/types';
import { type AgentInboundMessageMetadata } from '@air/server-sdk';

/**
 * What is on screen at send time. `filters.board` is direct members and
 * `filters.boardTree` includes nested boards. This is not a cited selection.
 * The SDK names this `AgentVisualContext` but does not re-export it, so this
 * reads the field on the inbound metadata type.
 */
export type WorkspaceAgentVisualContext = NonNullable<AgentInboundMessageMetadata['visualContext']>;

type WorkspaceAgentVisualContextSort = NonNullable<WorkspaceAgentVisualContext['sort']>;

const QUERY_MAX_LENGTH = 2000;
const SORT_NAME_MAX_LENGTH = 64;

type BuildWorkspaceAgentVisualContextParams = {
  assetView: boolean;
  boardId: string | undefined;
  libraryId: string | undefined;
  query: string | undefined;
  clipsFilters: Record<string, unknown>;
  hasUserFilters: boolean;
  isFlattenedView: boolean;
  selectAll: boolean;
  includeNestedItems: boolean;
  sort: SortField | undefined;
  timeZone: string | undefined;
};

const readQuery = (query: string | undefined): string | undefined => {
  const trimmed = query?.trim();

  if (!trimmed) {
    return undefined;
  }

  return trimmed.length > QUERY_MAX_LENGTH ? trimmed.slice(0, QUERY_MAX_LENGTH) : trimmed;
};

const readTimeZone = (timeZone: string | undefined): string | undefined => {
  const trimmed = timeZone?.trim();

  if (!trimmed) {
    return undefined;
  }

  return trimmed;
};

const readSort = (sort: SortField | undefined): WorkspaceAgentVisualContextSort | undefined => {
  const name = sort?.name;

  if (sort === undefined || name === undefined || name.length < 1 || name.length > SORT_NAME_MAX_LENGTH) {
    return undefined;
  }

  if (sort.direction === SortDirection.asc) {
    return { direction: 'asc', name };
  }

  if (sort.direction === SortDirection.desc) {
    return { direction: 'desc', name };
  }

  return undefined;
};

const withoutSimilarity = (filters: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(filters).filter(([key]) => key !== 'similarTo' && key !== 'similarToImage'));

const withBoardMembership = ({
  boardId,
  expandNested,
  filters,
}: {
  boardId: string | undefined;
  expandNested: boolean;
  filters: Record<string, unknown>;
}): Record<string, unknown> => {
  if (!boardId) {
    return filters;
  }

  const hasMembership = filters.board !== undefined || filters.boardTree !== undefined;

  if (hasMembership && !(expandNested && filters.boardTree === undefined)) {
    return filters;
  }

  const { board: _board, boardTree: _boardTree, ...rest } = filters;

  return {
    ...rest,
    ...(expandNested ? { boardTree: { is: boardId } } : { board: { is: boardId } }),
  };
};

const withLibraryMembership = ({
  filters,
  libraryId,
}: {
  filters: Record<string, unknown>;
  libraryId: string | undefined;
}): Record<string, unknown> =>
  libraryId === undefined || filters.library !== undefined ? filters : { ...filters, library: { is: libraryId } };

const viewIdentity = ({
  assetView,
  boardId,
  libraryId,
}: {
  assetView: boolean;
  boardId: string | undefined;
  libraryId: string | undefined;
}): WorkspaceAgentVisualContext => {
  if (assetView) {
    if (boardId !== undefined) {
      return { boardId, surface: 'asset' };
    }

    if (libraryId !== undefined) {
      return { libraryId, surface: 'asset' };
    }

    return { surface: 'asset' };
  }

  if (boardId !== undefined) {
    return { boardId, surface: 'board' };
  }

  if (libraryId !== undefined) {
    return { libraryId, surface: 'library' };
  }

  return { surface: 'all' };
};

export const buildWorkspaceAgentVisualContext = ({
  assetView,
  boardId,
  clipsFilters,
  hasUserFilters,
  includeNestedItems,
  isFlattenedView,
  libraryId,
  query,
  selectAll,
  sort,
  timeZone,
}: BuildWorkspaceAgentVisualContextParams): WorkspaceAgentVisualContext => {
  const headerQuery = readQuery(query);
  const expandNested =
    isFlattenedView || headerQuery !== undefined || hasUserFilters || (selectAll && includeNestedItems);
  const filters = withLibraryMembership({
    filters: withBoardMembership({
      boardId,
      expandNested,
      filters: withoutSimilarity(clipsFilters),
    }),
    libraryId,
  });
  const viewSort = readSort(sort);
  const viewerTimeZone = readTimeZone(timeZone);
  const identity = viewIdentity({
    assetView,
    boardId,
    libraryId,
  });

  return {
    ...identity,
    ...(headerQuery === undefined ? {} : { query: headerQuery }),
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
    ...(viewSort ? { sort: viewSort } : {}),
    ...(selectAll ? ({ selection: 'all' } as const) : {}),
    ...(viewerTimeZone === undefined ? {} : { timeZone: viewerTimeZone }),
  };
};
